import { type GlassesRenderingOptions } from "types"
import { Effect } from "@banuba/webar"
import { applyEffect } from "./player"

type ApplyOptions = {
  isCurrent?: () => boolean
}

export type RuntimeGlassesControls = {
  positionX: number
  positionY: number
  positionZ: number
  scaleX: number
}

let currentEffect: Effect | null = null
const READ_RENDERING_ATTEMPTS = 10
const READ_RENDERING_DELAY_MS = 120

const shouldApply = (options?: ApplyOptions) => options?.isCurrent?.() ?? true

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

export const detachCurrentEffect = () => {
  currentEffect = null
}

export const applyGlassesZipEffect = async (zipEffect: Blob, options?: ApplyOptions) => {
  if (!shouldApply(options)) return

  const effect = new Effect(zipEffect)
  if (!shouldApply(options)) return

  currentEffect = null
  await applyEffect(effect)
  if (!shouldApply(options)) return

  currentEffect = effect
}

export const resetGlasses = async () => {
  currentEffect = null
}

export const setGlassesRenderingOptions = async (
  renderingOptions: GlassesRenderingOptions,
  options?: ApplyOptions
) => {
  if (!shouldApply(options)) return

  await setGlassesScale(renderingOptions.scale.x, options)

  const {
    positionAccuracy: { x: positionX, y: positionY, z: positionZ },
  } = renderingOptions
  await setGlassesPosition(positionX, positionY, positionZ, options)
}

export const readRuntimeGlassesControls = async (
  options?: ApplyOptions
): Promise<RuntimeGlassesControls | null> => {
  if (!shouldApply(options)) return null

  const effect = currentEffect
  if (!effect) return null

  const readScript = `(() => {
    try {
      if (
        typeof bnb === "undefined" ||
        !bnb.scene ||
        typeof bnb.scene.getRoot !== "function" ||
        typeof bnb.scene.getAssetManager !== "function"
      )
        return ""

      const glasses = bnb.scene.getRoot().findChildByName("glasses")
      if (!glasses || typeof glasses.getComponent !== "function") return ""

      const transformComponent = glasses.getComponent(bnb.ComponentType.TRANSFORMATION)
      if (!transformComponent || typeof transformComponent.asTransformation !== "function") return ""

      const transform = transformComponent.asTransformation()
      const translation = transform.getTranslation()
      const transformScale = transform.getScale()
      let scaleX = Number(transformScale.x)

      try {
        const matFront = bnb.scene.getAssetManager().findMaterial("mat_front")
        const jsParams =
          matFront && typeof matFront.findParameter === "function"
            ? matFront.findParameter("js_params")
            : null

        if (jsParams && typeof jsParams.getVector4 === "function") {
          const scaleVector = jsParams.getVector4()
          const jsScaleX = Number(scaleVector.x)
          if (Number.isFinite(jsScaleX)) scaleX = jsScaleX
        }
      } catch (error) {
        // Fall back to the transform component scale used by current digitized effects.
      }

      const result = {
        positionX: Number(translation.x),
        positionY: Number(translation.y),
        positionZ: Number(translation.z),
        scaleX,
      }

      if (!Object.values(result).every(Number.isFinite)) return ""

      return JSON.stringify(result)
    } catch (error) {
      return ""
    }
  })()`

  for (let attempt = 0; attempt < READ_RENDERING_ATTEMPTS; attempt += 1) {
    const rawValue = await effect.evalJs(readScript)
    if (!shouldApply(options)) return null

    if (rawValue)
      try {
        const parsedValue = JSON.parse(rawValue) as RuntimeGlassesControls
        if (
          [
            parsedValue.scaleX,
            parsedValue.positionX,
            parsedValue.positionY,
            parsedValue.positionZ,
          ].every(Number.isFinite)
        )
          return parsedValue
      } catch {
        // Continue retrying while the effect finishes activation.
      }

    if (attempt < READ_RENDERING_ATTEMPTS - 1) {
      await wait(READ_RENDERING_DELAY_MS)
      if (!shouldApply(options)) return null
    }
  }

  return null
}

export const setGlassesPosition = async (
  x: number,
  y: number,
  z: number,
  options?: ApplyOptions
) => {
  if (!shouldApply(options)) return

  const effect = currentEffect
  if (!effect) return

  await effect.evalJs(`setTranslation(${x}, ${y}, ${z})`)
}

export const setGlassesScale = async (x: number, options?: ApplyOptions) => {
  if (!shouldApply(options)) return

  const effect = currentEffect
  if (!effect) return

  await effect.evalJs(`setScale(${x})`)
}

export const applyDigitizedGlassesEffectFromUrl = async (
  effectUrl: string,
  options?: ApplyOptions
) => {
  const zipBlob = await fetch(effectUrl).then((res) => res.blob())
  if (!shouldApply(options)) return

  await applyGlassesZipEffect(zipBlob, options)
}
